import { Palette } from '@core/config/constants';

/** Una pregunta de la trivia con sus opciones y el indice de la respuesta correcta. */
export interface TriviaQuestion {
  readonly question: string;
  readonly options: readonly string[];
  readonly correctIndex: number;
}

/**
 * Banco de preguntas (genericas y aptas para 11-14 años, sin referencias a artistas
 * reales). Para agregar preguntas, sumar objetos a este arreglo.
 */
export const TRIVIA_QUESTIONS: readonly TriviaQuestion[] = [
  {
    question: '¿Cuántas notas tiene la escala musical básica (Do Re Mi...)?',
    options: ['5', '7', '8', '12'],
    correctIndex: 1,
  },
  {
    question: '¿Qué instrumento tiene teclas blancas y negras?',
    options: ['Guitarra', 'Piano', 'Batería', 'Violín'],
    correctIndex: 1,
  },
  {
    question: '¿Cómo se llama la secuencia de pasos de un baile?',
    options: ['Melodía', 'Coreografía', 'Armonía', 'Partitura'],
    correctIndex: 1,
  },
  {
    question: '¿Qué color se forma al mezclar azul y amarillo?',
    options: ['Verde', 'Naranja', 'Violeta', 'Marrón'],
    correctIndex: 0,
  },
  {
    question: '¿Cuántos integrantes tiene un quinteto?',
    options: ['3', '4', '5', '6'],
    correctIndex: 2,
  },
  {
    question: '¿Qué se usa para grabar o amplificar la voz?',
    options: ['Auriculares', 'Micrófono', 'Parlante', 'Trípode'],
    correctIndex: 1,
  },
  {
    question: 'La parte de una canción que se repite y es más pegadiza se llama...',
    options: ['Estrofa', 'Puente', 'Estribillo', 'Intro'],
    correctIndex: 2,
  },
  {
    question: '¿Qué marca la velocidad de una canción?',
    options: ['El tempo', 'El color', 'La letra', 'El título'],
    correctIndex: 0,
  },
  {
    question: '¿Cuántas cuerdas tiene una guitarra clásica?',
    options: ['4', '5', '6', '8'],
    correctIndex: 2,
  },
  {
    question: '¿Qué instrumento se toca golpeando con baquetas?',
    options: ['Flauta', 'Batería', 'Arpa', 'Trompeta'],
    correctIndex: 1,
  },
  {
    question: '¿Cómo se llama la persona que dirige una orquesta?',
    options: ['Director', 'Cantante', 'Bailarín', 'Productor'],
    correctIndex: 0,
  },
  {
    question: '¿Qué color se forma al mezclar rojo y blanco?',
    options: ['Rosa', 'Naranja', 'Violeta', 'Gris'],
    correctIndex: 0,
  },
  {
    question: 'En el baile, ¿qué significa estar "en sincro"?',
    options: ['Cantar fuerte', 'Moverse todos a la vez', 'Saltar alto', 'Ir despacio'],
    correctIndex: 1,
  },
  {
    question: '¿Qué nota sigue después de "Sol"?',
    options: ['Fa', 'La', 'Do', 'Mi'],
    correctIndex: 1,
  },
  {
    question: '¿Dónde se presentan los artistas ante el público?',
    options: ['En el camerino', 'En el escenario', 'En el estudio', 'En la oficina'],
    correctIndex: 1,
  },
  {
    question: '¿Qué se usa para iluminar un show en vivo?',
    options: ['Reflectores', 'Espejos', 'Ventiladores', 'Pantallas táctiles'],
    correctIndex: 0,
  },
  {
    question: '¿Qué color se forma al mezclar rojo y azul?',
    options: ['Verde', 'Violeta', 'Naranja', 'Celeste'],
    correctIndex: 1,
  },
  {
    question: '¿Cómo se llama un grupo de tres cantantes?',
    options: ['Dúo', 'Trío', 'Cuarteto', 'Solista'],
    correctIndex: 1,
  },
  {
    question: '¿Qué hace falta para que una canción tenga "ritmo"?',
    options: ['Un pulso constante', 'Muchos colores', 'Un micrófono caro', 'Luces'],
    correctIndex: 0,
  },
  {
    question: '¿Qué prenda se usa en los pies para bailar?',
    options: ['Guantes', 'Zapatillas', 'Sombrero', 'Bufanda'],
    correctIndex: 1,
  },
  {
    question: 'Cantar muy suave y bajito se dice...',
    options: ['Fuerte', 'Susurrar', 'Gritar', 'Agudo'],
    correctIndex: 1,
  },
  {
    question: '¿Qué nota va antes de "Re"?',
    options: ['Do', 'Mi', 'Fa', 'La'],
    correctIndex: 0,
  },
  {
    question: '¿Cómo se llama el grupo de personas que mira un show?',
    options: ['El público', 'El elenco', 'La banda', 'El jurado'],
    correctIndex: 0,
  },
  {
    question: '¿Qué instrumento NO es de cuerda?',
    options: ['Guitarra', 'Violín', 'Flauta', 'Arpa'],
    correctIndex: 2,
  },
  {
    question: '¿Qué significa ensayar?',
    options: ['Practicar antes del show', 'Comprar ropa', 'Dormir', 'Comer'],
    correctIndex: 0,
  },
  {
    question: 'Un sonido muy alto (como un silbato) es un sonido...',
    options: ['Grave', 'Agudo', 'Lento', 'Suave'],
    correctIndex: 1,
  },
  {
    question: '¿Qué se necesita para un buen trabajo en equipo en el escenario?',
    options: ['Coordinación', 'Competir', 'Ignorarse', 'Apurarse'],
    correctIndex: 0,
  },
];

/** Configuracion del minijuego de trivia. */
export const TriviaConfig = {
  key: 'TriviaScene',
  title: 'Trivia Musical',
  description: 'Responde preguntas de música, baile y mucho más.',
  icon: '🧠',
  color: Palette.highlight,

  /** Cantidad de preguntas por partida (se eligen al azar del banco). */
  questionsPerRound: 8,
  /** Puntos por respuesta correcta. */
  pointsPerCorrect: 20,
  /** Tiempo (ms) que se muestra el feedback antes de pasar a la siguiente. */
  feedbackDelayMs: 1100,
  /** Monedas otorgadas al finalizar por cada 100 puntos. */
  coinsPer100Points: 1,
} as const;
